import type { WorkspaceViewId } from "../navItems";
import { viewAfterNodeSelect } from "../searchSelectionNavigation";

/**
 * The registry identity of one entity the assistant read, as far as opening it
 * is concerned: where its latest node lives.
 */
export type PrivateAgentTouchedEntityTarget = {
  latestFrameRecordId: string;
  latestNodeId: string;
};

export type PrivateAgentTouchedEntitySelection =
  | { kind: "in-frame"; nodeId: string }
  | { kind: "cross-frame"; frameRecordId: string; nodeId: string; nextView: WorkspaceViewId };

/**
 * Where opening one touched entity has to go, and nothing else — the route owns
 * every side effect. Two things make a target reachable without leaving the
 * loaded frame: the registry's latest frame *is* the loaded one, or the loaded
 * frame already carries a node for the same canonical id under a different
 * frame record. Anything else is a cross-frame jump, and `nextView` keeps the
 * user in Chat instead of forcing the graph view.
 */
export function privateAgentTouchedEntitySelection(input: {
  activeView: WorkspaceViewId;
  entry: PrivateAgentTouchedEntityTarget;
  loadedFrameRecordId: string | null;
  loadedNodeIdForCanonicalId: string | null;
}): PrivateAgentTouchedEntitySelection {
  const { activeView, entry, loadedFrameRecordId, loadedNodeIdForCanonicalId } = input;

  if (entry.latestFrameRecordId === loadedFrameRecordId || loadedNodeIdForCanonicalId) {
    return { kind: "in-frame", nodeId: loadedNodeIdForCanonicalId ?? entry.latestNodeId };
  }

  return {
    kind: "cross-frame",
    frameRecordId: entry.latestFrameRecordId,
    nodeId: entry.latestNodeId,
    nextView: viewAfterNodeSelect(activeView)
  };
}
